import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CustomButton from "@components/buttons/CustomButton";
import { CurrencyLogoDropdown } from "@components/dropdowns/CurrencyLogoDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { getApiErrorDisplay } from "@services/api/apiError";
import { transactionApiServices } from "@services/apis/Transactions";
import axios from "axios";
import { GetPHPRateHistoryData } from "@services/models/Transactions/GetPHPRateHistory";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSnackbarMessage from "src/utils/HandleSuccessErrorSnackbars";
import ConstantFunctions from "src/utils/constants_functions";

const CrossRampRates = () => {
  const constantFunctions = useMemo(() => new ConstantFunctions(), []);
  const gridRef = useRef<AgGridReact>(null);
  const [rate, setRate] = useState<string>("");
  const [currencySymbol, setCurrencySymbol] = useState<string>("PHP");
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [rateHistory, setRateHistory] = useState<GetPHPRateHistoryData[]>([]);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const currencyOptions = useMemo(
    () => [
      {
        id: "PHP",
        currency: "PHP",
        currencyLogo: "",
        isToken: false,
      },
    ],
    [],
  );

  const fetchRateHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await transactionApiServices.getPHPRateHistory({
        currencySymbol,
        limit: 1000,
        offset: 0,
      });
      setRateHistory(response.data || []);
    } catch (error: unknown) {
      if (!axios.isAxiosError(error)) {
        handleError({ message: getApiErrorDisplay(error) });
      }
    } finally {
      setLoading(false);
    }
  }, [currencySymbol]);

  useEffect(() => {
    fetchRateHistory();
  }, []);

  const handleSetRate = async () => {
    const parsedRate = Number(rate);
    if (!Number.isFinite(parsedRate) || parsedRate <= 0) {
      handleError({ message: "Please enter a valid rate." });
      return;
    }
    setBtnLoading(true);
    try {
      const response = await transactionApiServices.setPHPRate({
        currencySymbol,
        usdtToFiatRate: parsedRate,
      });
      setRate("");
      handleSuccess({ message: response.msg || "Rate set successfully." });
      fetchRateHistory();
    } catch (error: unknown) {
      if (!axios.isAxiosError(error)) {
        handleError({ message: getApiErrorDisplay(error) });
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Created At",
        field: "createdAt",
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Fiat",
        field: "fiatCurrencySymbol",
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center h-full gap-2">
              <img
                src={data.fiatCurrencyImage}
                alt={data.fiatCurrencySymbol}
                height={14}
                width={14}
              />
              <span className="text-[10px] text-text-base font-medium truncate">
                {data.fiatCurrencySymbol}
              </span>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        minWidth: 300,
      },
      {
        headerName: "Crypto",
        field: "cryptoCurrencySymbol",
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center h-full gap-2">
              <img
                src={data.cryptoCurrencyImage}
                alt={data.cryptoCurrencySymbol}
                height={14}
                width={14}
              />
              <span className="text-[10px] text-text-base font-medium truncate">
                {data.cryptoCurrencySymbol}
              </span>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        minWidth: 300,
      },
      {
        headerName: "USDT to Fiat",
        field: "usdtToFiatRate",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        minWidth: 300,
      },
      {
        headerName: "Fiat to USDT",
        field: "fiatToUsdtRate",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
    ],
    [constantFunctions],
  );

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <p className="flex text-text-base text-[14px] font-semibold items-center">
          Cross Ramp Rates
        </p>
      </div>
      <div
        className="flex flex-col gap-4 px-3 min-h-0 w-full overflow-hidden"
        style={{ height: "calc(100dvh - 90px)" }}
      >
        <div className="shrink-0 bg-bg-page-body rounded-lg w-full">
          <h2 className="text-text-base text-[14px] font-bold mt-4">
            Set Rate
          </h2>
          <div className="grid grid-cols-3 w-[55%] gap-3 items-start">
            <div className="flex flex-col gap-1">
              <CurrencyLogoDropdown
                listOfValues={currencyOptions}
                value={currencySymbol}
                onChange={(value) => setCurrencySymbol(String(value))}
                label="Currency"
                color="bg-main-container-header"
                baseColor="bg-bg-page-body"
                borderColor="border-subtle"
                expandedBorderColor="border-button-primary"
                hideLogo
              />
            </div>
            <div>
              <GlobalTextInputField
                type="text"
                label="Exchange Rate"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="1 USDT = x PHP"
                insideClassName="w-full h-[48px] py-[10px]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium opacity-0">Spacer</label>
              <CustomButton
                isActive={Boolean(rate && currencySymbol)}
                text="Set Rate"
                onClick={handleSetRate}
                className="w-full h-[48px]"
                isButtonLoading={btnLoading}
              />
            </div>
          </div>
        </div>
        <div className="flex-1 min-h-0 min-w-0 w-full">
          {loading && rateHistory.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={rateHistory}
              columnDefs={columnDefs}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CrossRampRates;
